import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { PrintDao } from "@/data/dao/service/PrintDao";
import { Language, ReceiptType } from "@/data/model/Enums";
import { exceptionTypes } from "@/data/util/Config";

import { BaseModal } from "@/shared/elements/BaseModal";
import { languageSelectList } from "@/shared/localization/language";
import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { Popup } from "@/components/dialog/Popup";

import { OperatorClient } from "@/operator/OperatorClient";
import { SelectBootstrap } from "@/operator/elements/SelectBootstrap";
import { trans } from "@/operator/localization/Trans";

export class PrintOrderReceiptWindow extends BaseModal {
    private printDao = Factory.getInstance<PrintDao>(PrintDao);

    private sourceId: string;
    private readonly receiptType: ReceiptType;
    private $form: JQuery;
    private $submitBtn: JQuery;

    private languageSelect: SelectBootstrap<Language>;

    constructor(sourceId: string, receiptType: ReceiptType) {
        super(trans.printOrderReceipt.title, "modal-lg", false);
        this.sourceId = sourceId;
        this.receiptType = receiptType;

        this.$element.addClass("printorderreceipt");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);

        const createSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        createSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                trans.printOrderReceipt.settings +
                "</h3></div></div>"
        );

        const languageGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                trans.printOrderReceipt.language +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        const select = $('<select class="form-control" />').appendTo(languageGroup);
        languageSelectList.forEach((item) => {
            select.append(`<option value="${item.id}">${trans.enums.language[item.name]}</option>`);
        });
        this.languageSelect = SelectBootstrap.createFromSelect(select);
        this.languageSelect.setValue(OperatorClient.context.settings!.location!.language);

        $('<button type="button" class="btn btn-secondary">' + trans.general.cancel + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $(
            '<button type="button" class="btn btn-success">' + trans.printOrderReceipt.printReceipt + "</button>"
        )
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            switch (this.receiptType) {
                case ReceiptType.Order:
                    this.printOrderReceipt();
                    break;
                case ReceiptType.Payment:
                    this.printPaymentReceipt();
                    break;
            }

            return false;
        });
    }

    protected onShow() {}

    protected onHide() {}

    private async printPaymentReceipt() {
        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        try {
            await this.printDao.printPaymentReceipt(this.sourceId, {
                locationId: OperatorClient.context.settings!.location!.id,
                language: parseInt((this.languageSelect.getValue() ?? "") as any),
            });

            Popup.show({
                title: "",
                message: translations.print.orderReceiptPrinted,
            });
        } catch (ex) {
            logError(ex);
            const e = ex as DaoError;
            if (e.type === exceptionTypes.noProductionSiteForRoomEnabledException) {
                Popup.show({
                    title: trans.general.failed,
                    message: trans.print.noProductionSiteEnabledForRoom,
                });
            } else {
                Popup.show({
                    title: trans.general.failed,
                    message: translations.print.failedPrintReceipt,
                });
            }
        } finally {
            this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
            this.close();
        }
    }

    private async printOrderReceipt() {
        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        try {
            await this.printDao.printOrderReceipt(this.sourceId, {
                locationId: OperatorClient.context.settings!.location!.id,
                language: parseInt((this.languageSelect.getValue() ?? "") as any),
            });

            Popup.show({
                title: "",
                message: translations.print.orderReceiptPrinted,
            });
        } catch (ex) {
            logError(ex);
            const e = ex as DaoError;
            if (e.type === exceptionTypes.noProductionSiteForRoomEnabledException) {
                Popup.show({
                    title: trans.general.failed,
                    message: trans.print.noProductionSiteEnabledForRoom,
                });
            } else {
                Popup.show({
                    title: trans.general.failed,
                    message: translations.print.failedPrintReceipt,
                });
            }
        } finally {
            this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
            this.close();
        }
    }
}
